<template>
  <div class="wage-process-container">
    <!-- 로딩 컴포넌트 -->
    <LodingComponent :is-visible="isLoading" :text="loadingText" />
    
    <!-- 임금관리 현황 카드 -->
    <v-card class="file-upload-card-dense" elevation="1">
      <div class="dense-header">
        <div class="title-group">
          <v-icon color="#64748b" size="20">mdi-chart-timeline-variant</v-icon>
          <div class="title-text">
            <h2>{{ selectedYear }}년 임금관리 현황</h2>
            <span>월별 임금 관리 프로세스 및 마감 상태를 확인하세요</span>
          </div>
        </div>
      </div>
    
      <div class="months-dense-grid">
        <div
          v-for="month in 12"
          :key="month"
          class="month-card-dense"
          :class="{ 
            'current': month === currentMonth,
            'completed': getMonthStatus(month).isClosed,
            'closed': getMonthStatus(month).isClosed
          }"
          @click="handleMonthClick(month)"
        >
          <!-- 카드 앞면 -->
          <div class="month-card-front" style="cursor: pointer;">
            <div class="month-card-top">
              <div class="month-title">
                <span class="month-number">{{ month }}월</span>
                <v-chip
                  v-if="month === currentMonth"
                  color="#1e3a8a"
                  size="x-small"
                  variant="flat"
                >
                  NOW
                </v-chip>
                <v-chip
                  v-if="getMonthStatus(month).isClosed"
                  color="#0f172a"
                  size="x-small"
                  variant="flat"
                  class="ml-1"
                >
                  마감완료
                </v-chip>
              </div>
            </div>

            <div class="month-card-upload-area">
              <!-- 마감완료 상태 -->
              <v-btn
                v-if="getMonthStatus(month).isClosed"
                variant="tonal"
                color="#1e3a8a"
                size="small"
                prepend-icon="mdi-lock-check"
                class="file-submit-btn"
              >
                마감완료
              </v-btn>
              
              <!-- 진행중 상태 -->
              <v-btn
                v-else-if="getMonthStatus(month).hasProcess"
                variant="tonal"
                color="#1e3a8a"
                size="small"
                prepend-icon="mdi-progress-check"
                class="file-submit-btn"
              >
                진행중 (Step {{ getMonthStatus(month).currentStep }}/4)
              </v-btn>
              
              <!-- 미진행 상태 -->
              <v-btn
                v-else
                variant="tonal"
                color="grey"
                size="small"
                prepend-icon="mdi-minus-circle"
                disabled
                class="file-submit-btn"
              >
                미진행
              </v-btn>

              <!-- 진행률 표시 -->
              <div v-if="getMonthStatus(month).hasProcess || getMonthStatus(month).isClosed" class="progress-info">
                <v-progress-linear
                  :model-value="getMonthStatus(month).progress"
                  :color="getMonthStatus(month).isClosed ? '#10b981' : '#1e3a8a'"
                  height="4"
                  class="progress-bar-small"
                ></v-progress-linear>
                <span class="progress-text-small" style="z-index: 1; background-color: white;">{{ Math.round(getMonthStatus(month).progress) }}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </v-card>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import axios from 'axios';
import LodingComponent from '@/components/loading/LodingComponent.vue';

interface WageProcessData {
  wp_ym: string;
  wp_step1_completed: number;
  wp_step2_completed: number;
  wp_step3_completed: number;
  wp_step4_completed: number;
  wp_current_step: number;
}

interface WageMagamData {
  wm_ym: string;
  wm_job_type: string;
  wm_count: string;
  wm_wage_sum: string;
  wm_isuse: 'y' | 'n';
}

interface MonthStatus {
  hasProcess: boolean;
  currentStep: number;
  progress: number;
  isClosed: boolean;
  employeeCount: number;
  totalWage: number;
}

const props = defineProps<{
  selectedYear: number;
}>();

const store = useStore();
const router = useRouter();

const isLoading = ref(false);
const loadingText = ref('데이터를 불러오는 중...');
const currentMonth = ref(new Date().getMonth() + 1);
const wageProcessData = ref<WageProcessData[]>([]);
const wageMagamData = ref<WageMagamData[]>([]);

// 월별 상태 계산
const getMonthStatus = (month: number): MonthStatus => {
  const ym = `${props.selectedYear}${String(month).padStart(2, '0')}`;
  
  // 프로세스 데이터 찾기
  const processData = wageProcessData.value.find(p => p.wp_ym === ym);
  
  // 마감 데이터 찾기 (전체합계)
  const magamData = wageMagamData.value.find(
    m => m.wm_ym === ym && m.wm_job_type === '전체합계' && m.wm_isuse === 'y'
  );
  
  // 프로세스가 있는 경우
  if (processData) {
    const completedSteps = [
      processData.wp_step1_completed,
      processData.wp_step2_completed,
      processData.wp_step3_completed,
      processData.wp_step4_completed
    ].filter(s => s === 1).length;
    
    return {
      hasProcess: true,
      currentStep: processData.wp_current_step || completedSteps,
      progress: (completedSteps / 4) * 100,
      isClosed: !!magamData,
      employeeCount: magamData ? parseInt(magamData.wm_count) || 0 : 0,
      totalWage: magamData ? parseInt(magamData.wm_wage_sum) || 0 : 0
    };
  }
  
  // 마감만 있는 경우
  if (magamData) {
    return {
      hasProcess: false,
      currentStep: 4,
      progress: 100,
      isClosed: true,
      employeeCount: parseInt(magamData.wm_count) || 0,
      totalWage: parseInt(magamData.wm_wage_sum) || 0
    };
  }
  
  // 아무것도 없는 경우
  return {
    hasProcess: false,
    currentStep: 0,
    progress: 0,
    isClosed: false,
    employeeCount: 0,
    totalWage: 0
  };
};

// 월 클릭 핸들러
const handleMonthClick = (month: number) => {
  const ym = `${props.selectedYear}${String(month).padStart(2, '0')}`;
  
  // Store에 연월 정보 설정 (YearMonthSelector가 이 값을 사용)
  store.commit('setLebYm', ym);
  
  // 라우터 이동
  router.push('/pgm/wage/WageProcess');
};

// 데이터 조회
const fetchData = async () => {
  const user = store.state.user;
  if (!user || !user.m_sisul_code) {
    console.warn('사용자 정보가 없습니다.');
    return;
  }

  try {
    isLoading.value = true;
    loadingText.value = '임금관리 현황을 불러오는 중...';
    
    // 프로세스 데이터 조회 (해당 연도 1-12월)
    const processPromises = Array.from({ length: 12 }, (_, i) => {
      const month = i + 1;
      const ym = `${props.selectedYear}${String(month).padStart(2, '0')}`;
      
      return axios.get('/api/wage-process/status', {
        params: {
          sisulCode: user.m_sisul_code,
          yearMonth: ym
        },
        withCredentials: true
      }).then(res => res.data.data).catch(() => null);
    });
    
    const processResults = await Promise.all(processPromises);
    wageProcessData.value = processResults.filter(r => r !== null);
    
    // 마감 데이터 조회
    const magamResponse = await axios.get(`/api/calc/wage-magam-yearly/${user.m_sisul_code}/${props.selectedYear}`, {
      withCredentials: true
    });
    
    if (magamResponse.data.success) {
      wageMagamData.value = magamResponse.data.data || [];
    }
    
  } catch (error) {
    console.error('임금관리 현황 조회 오류:', error);
  } finally {
    isLoading.value = false;
  }
};

// 연도 변경 시 데이터 다시 조회
watch(() => props.selectedYear, () => {
  fetchData();
}, { immediate: true });

onMounted(() => {
  fetchData();
});
</script>

<style lang="scss" scoped>
@import '@/assets/styles/fileUpload.scss';
@import '@/assets/styles/fonts.scss';

.wage-process-container {
  font-family: $Noto_Sans_KR !important;
  
  // Vuetify 컴포넌트들에 폰트 적용
  :deep(.v-card),
  :deep(.v-btn),
  :deep(.v-chip),
  :deep(.v-progress-linear) {
    font-family: $Noto_Sans_KR !important;
    
    * {
      font-family: $Noto_Sans_KR !important;
    }
  }

  // 진행률 정보 (FileUpload에는 없지만 임금관리 현황에만 추가)
  .progress-info {
    margin-top: 8px;
    display: flex;
    align-items: center;
    gap: 8px;

    .progress-bar-small {
      flex: 1;
      border-radius: 2px;
    }

    .progress-text-small {
      font-size: 0.75rem;
      font-weight: 600;
      color: #6b7280;
      min-width: 32px;
      text-align: right;
    }
  }

  // 월별 세부 정보
  .month-info {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid #e5e7eb;
    display: flex;
    gap: 12px;

    .info-item {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 0.75rem;
      color: #6b7280;
      font-weight: 500;

      span {
        white-space: nowrap;
      }
    }
  }

}
</style>

